import React from 'react';
import { useParams } from 'react-router-dom';
import withModal, { ModalContext } from 'hoc/with-modal';
import SelectableNode from './components/selectable-node';
import Form from 'components/form';
import useCopySurfaceNodes from 'routes/surface/hooks/use-copy-surface-nodes';
import useCutSurfaceNodes from 'routes/surface/hooks/use-cut-surface-nodes';
import useCopySurfaceNodesByReference from 'routes/surface/hooks/use-copy-surface-nodes-by-reference';
import useFetchNodeFromCacheById from 'components/canvas/hooks/use-fetch-node-from-cache-by-id';
import { ClipboardAction, useCanvasClipboard } from 'state/canvas-clipboard';
import { useTranslation } from 'i18n';
import { Row, WithMargin } from 'components/layout';
import useCurrentSurfacePermissions from 'components/canvas/hooks/use-current-surface-permissions';
import useTranslateWindowPointToCanvas from 'components/canvas/hooks/use-translate-window-point-to-canvas';
import {
  Content,
  CopyIcon,
  CutIcon,
  NodesList,
  SubmitButton,
  SyncIcon,
  Title,
  ScrollableContainer,
} from './style';
import { SurfaceNodeUnion } from 'graphql/types';
import If from 'components/if-statement';
import { Position } from 'types';

interface ClipboardZoneModalProps {
  modal: ModalContext;
}

function ClipboardZoneModal({ modal }: ClipboardZoneModalProps) {
  const { t } = useTranslation();
  const { id: surface } = useParams<{ id: string }>();

  const [selectedNodes, setSelectedNodes] = React.useState<SurfaceNodeUnion[]>(
    [],
  );

  const fetchNodeFromCacheById = useFetchNodeFromCacheById(surface);

  const translateWindowPointToCanvas = useTranslateWindowPointToCanvas(surface);

  const copyNodes = useCopySurfaceNodes(surface);
  const cutNodes = useCutSurfaceNodes(surface);
  const copyNodesByReference = useCopySurfaceNodesByReference(surface);

  const [clipboardNodes, { clearClipboard }] = useCanvasClipboard();

  const { canPasteSurfaceNode } = useCurrentSurfacePermissions();

  const nodes = clipboardNodes.map(
    // @ts-ignore
    ({ id, surface, action, absolutePosition }) => {
      const node = fetchNodeFromCacheById({ id, surface: surface?.id });

      return { ...node, action, absolutePosition };
    },
  );

  const action = nodes[0]?.action;

  const noSelectedNodes = selectedNodes.length < 1;

  const isCutActionDisabled =
    canPasteSurfaceNode() === false || noSelectedNodes;

  const isCopyActionDisabled =
    canPasteSurfaceNode() === false || noSelectedNodes;

  const isReferenceCopyActionDisabled =
    canPasteSurfaceNode() === false || noSelectedNodes;

  function onSelectedNodes(input) {
    if (canPasteSurfaceNode()) {
      setSelectedNodes(input);
    }
  }

  function hideModal() {
    setSelectedNodes([]);
    modal.hide();
  }

  function clearClipboardAndHideModal() {
    clearClipboard();
    modal.hide();
  }

  async function onSubmit({ action }) {
    const operation = getOperation(action);

    const input = selectedNodes.map(createOperationInputForNode);

    try {
      const response = await operation(input);

      if (response.isSuccess) {
        clearClipboardAndHideModal();
      }

      return response;
    } catch (error) {
      //
    }
  }

  function createOperationInputForNode(node) {
    const actionOnSameSurface = surface === node?.surface?.id;

    // @ts-ignore
    const nodePosition = node.absolutePosition as Position;

    const position = actionOnSameSurface
      ? undefined
      : translateWindowPointToCanvas(nodePosition);

    return {
      id: node.id,
      surface,
      position: actionOnSameSurface ? undefined : position,
    };
  }

  function getOperation(action): any {
    switch (action) {
      case ClipboardAction.COPY:
        return copyNodes;
      case ClipboardAction.CUT:
        return cutNodes;
      case ClipboardAction.SYNC_COPY:
        return copyNodesByReference;
    }
  }

  return (
    <Form initialValues={{ action }} onSubmit={onSubmit}>
      {({ values: { action }, handleSubmit, setFieldValue }) => {
        const isCopy = [
          ClipboardAction.COPY,
          ClipboardAction.SYNC_COPY,
        ].includes(action);

        const isCut = action === ClipboardAction.CUT;

        return (
          <>
            <Form.Header>
              {isCut && (
                <>
                  <Form.Button
                    label={t('Close')}
                    onClick={hideModal}
                    secondary
                  />
                  <Row>
                    <WithMargin right="16px">
                      <Form.Button
                        label={t('Clear clipboard')}
                        onClick={clearClipboardAndHideModal}
                        secondary
                      />
                    </WithMargin>
                    <SubmitButton
                      iconLeft={<CutIcon disabled={isCutActionDisabled} />}
                      label={t('Insert cut')}
                      onClick={handleSubmit}
                      count={selectedNodes.length}
                    />
                  </Row>
                </>
              )}
              {isCopy && (
                <>
                  <Form.Button
                    label={t('Close')}
                    onClick={hideModal}
                    secondary
                  />
                  <Form.Button
                    label={t('Clear clipboard')}
                    onClick={clearClipboardAndHideModal}
                    secondary
                  />

                  <SubmitButton
                    iconLeft={<CopyIcon />}
                    label={t('Insert copy')}
                    onClick={handleSubmit}
                    disabled={isCopyActionDisabled}
                    count={selectedNodes.length}
                  />

                  <SubmitButton
                    iconLeft={<SyncIcon />}
                    label={t('Insert synced copy')}
                    onClick={() => {
                      setFieldValue('action', ClipboardAction.SYNC_COPY);
                      handleSubmit();
                    }}
                    disabled={isReferenceCopyActionDisabled}
                    count={selectedNodes.length}
                  />
                </>
              )}
            </Form.Header>
            <Content>
              <If condition={canPasteSurfaceNode()}>
                <If.Then>
                  <Title>{t('All elements in your clipboard')}</Title>
                </If.Then>
                <If.Else>
                  <Title red>
                    {t(
                      `You don't have permission to paste nodes on this surface`,
                    )}
                  </Title>
                </If.Else>
              </If>

              <ScrollableContainer>
                <NodesList>
                  {nodes.map((node: any) => (
                    <SelectableNode
                      key={node.id}
                      node={node}
                      setSelectedNodes={onSelectedNodes}
                      selectedNodes={selectedNodes}
                      disabled={canPasteSurfaceNode() === false}
                    />
                  ))}
                </NodesList>
              </ScrollableContainer>
            </Content>
          </>
        );
      }}
    </Form>
  );
}

export default withModal(ClipboardZoneModal);
